import React from "react";
import { SeoMeta } from "../components/SeoMeta";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Image as ImageIcon, Info, Clock, CalendarX } from "lucide-react";
import { SERVICES, SITE, FAQ } from "../mock";

export default function Services() {
  return (
    <div className="relative">
      <SeoMeta 
        title="Služby a ceny fyzioterapie | Zdravé packy Nový Bohumín"
        description="Přehled služeb a ceník fyzioterapie zvířat v Novém Bohumíně. Vyberte si rehabilitační terapii pro vašeho chlupáče."
        url="https://www.zdravepacky.cz/services"
      />
      <section className="pt-10 pb-6">
        <div className="mx-auto max-w-6xl px-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Služby a ceny</h1>
              <p className="text-muted-foreground mt-2">Vyberte si terapii v Novém Bohumíně, která vašemu chlupáči nejlépe sedí.</p>
            </div>
            <div className="inline-flex items-center self-start sm:self-auto gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-500/20 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse"></span>
              Ceník platný od 1.10.2026
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-8">
            {SERVICES.map((s) => {
              return (
                <Card key={s.id} className="bg-white/10 backdrop-blur-xl border-white/20 overflow-hidden flex flex-col">
                  <CardContent className="p-6 flex flex-col flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h2 className="text-xl font-semibold text-foreground">{s.title}</h2>
                        {s.duration && (
                          <span className="inline-block text-xs font-medium text-muted-foreground mt-0.5">{s.duration}</span>
                        )}
                      </div>
                      <div className="text-lg font-semibold text-blue-700/90 dark:text-blue-300 whitespace-nowrap">{s.price}</div>
                    </div>

                    {s.title === "Úvodní konzultace" && (
                      <p className="text-xs font-bold text-blue-700 dark:text-blue-300 mt-2">
                        (nutno zvolit, pokud se k nám objednáváte poprvé)
                      </p>
                    )}

                    {s.includes && s.includes.length > 0 && (
                      <div className="mt-4 flex-1">
                        {s.includesLabel && (
                          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                            {s.includesLabel}
                          </p>
                        )}
                        <ul className="grid gap-1.5 text-sm list-disc pl-5 text-foreground/80">
                          {s.includes.map((i, idx) => (
                            <li key={idx}>{i}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="mt-auto pt-6 flex flex-wrap gap-3">
                      <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white">
                        <a href={SITE.bookingUrl} target="_blank" rel="noopener noreferrer">Objednat</a>
                      </Button>
                      <Button variant="outline" asChild>
                        <a href={`mailto:${SITE.contacts.email}?subject=Poptávka: ${s.title}`}>Napsat e‑mail</a>
                      </Button>
                      
                      {((s.keyPoints && s.keyPoints.length > 0) || (s.subSections && s.subSections.length > 0)) && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="secondary">Více informací</Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-3xl md:max-w-4xl max-w-[95vw] max-h-[90vh] overflow-y-auto p-6 md:p-8">
                            <DialogHeader className="border-b border-border/40 pb-4">
                              <div className="flex flex-wrap items-baseline justify-between gap-2 pr-6">
                                <DialogTitle className="text-2xl font-bold tracking-tight">{s.title}</DialogTitle>
                                {s.price && (
                                  <span className="text-lg font-semibold text-blue-700/90 dark:text-blue-300">
                                    {s.price}
                                  </span>
                                )}
                              </div>
                            </DialogHeader>

                            <div className="grid md:grid-cols-12 gap-6 mt-4 items-start">
                              {/* Levý sloupec: Fotka nebo Placeholder */}
                              <div className="md:col-span-5 flex flex-col gap-4">
                                {s.image ? (
                                  <div className={`overflow-hidden rounded-xl border border-white/20 shadow-md ${
                                    s.isPortrait ? "h-[400px] sm:h-[460px] md:h-[500px]" : "h-60 sm:h-64 md:h-72"
                                  }`}>
                                    <img 
                                      src={s.image} 
                                      alt={s.title} 
                                      style={s.imagePosition ? { objectPosition: s.imagePosition } : undefined}
                                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" 
                                    />
                                  </div>
                                ) : (
                                  <div className={`${
                                    s.isPortrait ? "h-[400px] sm:h-[460px] md:h-[500px]" : "h-60 sm:h-64 md:h-72"
                                  } rounded-xl border border-dashed border-white/20 bg-muted/10 flex flex-col items-center justify-center gap-2.5 p-6 text-center text-muted-foreground shadow-sm`}>
                                    <div className="w-14 h-14 rounded-full bg-white/10 dark:bg-white/5 border border-white/20 flex items-center justify-center text-muted-foreground/70">
                                      <ImageIcon className="w-7 h-7" />
                                    </div>
                                    <span className="text-xs font-medium text-muted-foreground/80">[Bude doplněna fotografie]</span>
                                  </div>
                                )}
                                <Button asChild className="hidden md:flex w-full bg-blue-600 hover:bg-blue-700 text-white">
                                  <a href={SITE.bookingUrl} target="_blank" rel="noopener noreferrer">Objednat</a>
                                </Button>
                              </div>

                              {/* Pravý sloupec: Odrážky od klientky */}
                              <div className="md:col-span-7 flex flex-col gap-4">
                                {s.subSections ? (
                                  <div className="space-y-3.5">
                                    {s.subSections.map((sub, subIdx) => (
                                      <div key={subIdx} className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                                        <h4 className="text-xs font-bold uppercase tracking-wider text-blue-700 dark:text-blue-300 mb-2.5 flex items-center gap-2">
                                          <span className="inline-block w-2 h-2 rounded-full bg-blue-600"></span>
                                          {sub.title}
                                        </h4>
                                        <ul className="space-y-1.5 text-sm text-foreground/90">
                                          {sub.points.map((point, pIdx) => (
                                            <li key={pIdx} className="flex items-start gap-2">
                                              <span className="text-blue-600 dark:text-blue-400 font-bold shrink-0">✓</span>
                                              <span>{point}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  s.keyPoints && s.keyPoints.length > 0 && (
                                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                                      <h4 className="text-xs font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-300 mb-2.5 flex items-center gap-2">
                                        <span className="inline-block w-2 h-2 rounded-full bg-blue-600"></span>
                                        Hlavní body a přínosy
                                      </h4>
                                      <ul className="space-y-2 text-sm text-foreground/90">
                                        {s.keyPoints.map((point, pIdx) => (
                                          <li key={pIdx} className="flex items-start gap-2">
                                            <span className="text-blue-600 dark:text-blue-400 font-bold shrink-0">✓</span>
                                            <span>{point}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )
                                )}

                                <div className="block md:hidden pt-2">
                                  <Button asChild className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                                    <a href={SITE.bookingUrl} target="_blank" rel="noopener noreferrer">Objednat</a>
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}

                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="mt-12 mb-2 text-center">
            <p className="text-muted-foreground mt-2 italic">
              <strong>PŘEDPLATNÉ</strong> - Rehabilitační balíček - 10 aplikací/návštěv - <span className="font-bold text-blue-700/90 dark:text-blue-300">10% sleva</span>
            </p>
          </div>

          {/* Důležité organizační informace: Platnost a Storno */}
          <div className="mt-6 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 p-5 shadow-sm">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2 mb-3">
              <Info className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              Podmínky návštěv a platnosti
            </h4>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div className="flex items-start gap-3 bg-white/5 rounded-xl p-3.5 border border-white/10">
                <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-foreground">Platnost předplatného a poukazů</p>
                  <p className="mt-0.5 text-xs text-foreground/80 leading-relaxed">
                    Platnost předplacených balíčků a dárkových poukazů je <strong>6 měsíců</strong> od data zakoupení.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-white/5 rounded-xl p-3.5 border border-white/10">
                <CalendarX className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-foreground">Storno podmínky a zrušení termínu</p>
                  <p className="mt-0.5 text-xs text-foreground/80 leading-relaxed">
                    V případě zrušení termínu méně než <strong>24 hodin</strong> před domluvenou návštěvou může být lekce účtována v plné výši.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-10">
            <Accordion type="single" collapsible>
              {FAQ.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger>{f.q}</AccordionTrigger>
                  <AccordionContent>{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
}